import { Controller } from "@hotwired/stimulus";
import { get } from "@rails/request.js";

export default class extends Controller {
  static values = {
    dataUrl: String,
  };

  static targets = ["host", "warning"];

  async connect() {
    this.grids = new Map();
    this.overrides = new Map(); // row_number → bool, only explicit user changes
    this.riskyOverrides = new Set(); // row_number, subset of overrides: imported rows marked to upload anyway
    this.currentPage = 1;
    this.injectStatusStyles();
    this.bindModalGuard();

    const [slick] = await Promise.all([
      import("slickgrid"),
      import("slickgrid/dist/styles/css/slick.grid.css"),
      import("slickgrid/dist/styles/css/slick-default-theme.css"),
      import("slickgrid/dist/styles/css/slick.customtooltip.css"),
    ]);
    this.slick = slick;

    await this.waitForVisible();
    await this.loadData();
  }

  get gridHost() {
    return this.hasHostTarget ? this.hostTarget : this.element;
  }

  // "imported" and "changed" rows both match an existing record, so both
  // default to skipped (unchecked) — checking "Upload" on either creates a
  // new record with this file's values.
  isDuplicateStatus(status) {
    return status === "imported" || status === "changed";
  }

  waitForVisible() {
    return new Promise((resolve) => {
      if (this.gridHost.offsetWidth > 0) { resolve(); return; }
      const observer = new ResizeObserver((entries) => {
        if (entries[0].contentRect.width > 0) {
          observer.disconnect();
          resolve();
        }
      });
      observer.observe(this.gridHost);
    });
  }

  injectStatusStyles() {
    if (document.getElementById("upload-grid-status-styles")) return;
    const style = document.createElement("style");
    style.id = "upload-grid-status-styles";
    style.textContent = `
      .slick-row.row-status-imported     { background-color: #f8d7da !important; }
      .slick-row.row-status-changed      { background-color: #cfe2ff !important; }
      .slick-row.row-status-new          { background-color: #d4edda !important; }
      .slick-row.row-status-undetermined { background-color: #fff3cd !important; }
    `;
    document.head.appendChild(style);
  }

  disconnect() {
    this.grids.forEach(({ grid }) => grid.destroy());
    this.grids.clear();
    this.unbindModalGuard();
  }

  // The .modal element is a single instance shared app-wide (see application.html.erb),
  // so the guard must be bound/unbound alongside this controller's own lifecycle rather
  // than left attached — otherwise it would block dismissal of unrelated modals later.
  bindModalGuard() {
    this.$modal = window.$?.(this.element.closest(".modal"));
    this.handleModalHide = (event) => {
      if (this.overrides.size === 0) return;
      event.preventDefault();
      const discard = window.confirm(
        "You have unsaved row selection changes. They will be lost if you close this window. Continue?"
      );
      if (discard) {
        this.overrides.clear();
        this.riskyOverrides.clear();
        this.$modal?.modal("hide");
      }
    };
    this.$modal?.on("hide.bs.modal.uploadGrid", this.handleModalHide);
  }

  unbindModalGuard() {
    this.$modal?.off("hide.bs.modal.uploadGrid", this.handleModalHide);
  }

  async loadData() {
    const hostHeight = this.gridHost.offsetHeight;
    if (hostHeight > 0) this.gridHost.style.minHeight = `${hostHeight}px`;

    this.grids.forEach(({ grid }) => grid.destroy());
    this.grids.clear();
    this.gridHost.innerHTML = "";

    const url = new URL(this.dataUrlValue, window.location.origin);
    url.searchParams.set("page", this.currentPage);

    let data;
    try {
      const resp = await get(url.toString(), { responseKind: "json" });
      if (!resp.ok) {
        throw new Error(`Server returned ${resp.status}`);
      }
      data = await resp.json;
    } catch (err) {
      const alert = document.createElement("div");
      alert.className = "alert alert-danger";
      alert.textContent = `Failed to load grid data: ${err.message}`;
      this.gridHost.appendChild(alert);
      return;
    }

    const pagination = data.pagination;
    const totalPages = pagination?.total_pages ?? 1;

    if (data.file_errors && data.file_errors.length > 0) {
      const alert = document.createElement("div");
      alert.className = "alert alert-danger";
      const ul = document.createElement("ul");
      ul.className = "mb-0";
      data.file_errors.forEach((e) => {
        const li = document.createElement("li");
        li.textContent = e.message;
        ul.appendChild(li);
      });
      alert.appendChild(ul);
      this.gridHost.appendChild(alert);
    }

    const showSheetNames = data.sheets.length > 1;
    for (const sheet of data.sheets) {
      if (showSheetNames) {
        const header = document.createElement("h5");
        header.className = "mt-3 mb-1";
        header.textContent = sheet.name;
        this.gridHost.appendChild(header);
      }

      const ROW_HEIGHT = 25;
      const HEADER_HEIGHT = 25;
      const MIN_HEIGHT = 200;
      const MAX_HEIGHT = 500;
      const contentHeight = sheet.rows.length * ROW_HEIGHT + HEADER_HEIGHT;
      const height = Math.max(MIN_HEIGHT, Math.min(contentHeight, MAX_HEIGHT));

      const wrapper = document.createElement("div");
      wrapper.style.width = "100%";
      wrapper.style.height = `${height}px`;
      this.gridHost.appendChild(wrapper);

      const hasStatuses = Array.isArray(sheet.row_statuses) && sheet.row_statuses.length > 0;

      const columns = [
        ...(hasStatuses ? [{
          id: "_upload",
          name: "Upload",
          field: "_upload",
          width: 65,
          resizable: false,
          formatter: (_row, _cell, value) => ({
            text: `<input type="checkbox" ${value ? "checked" : ""} tabindex="-1">`,
          }),
        }] : []),
        {
          id: "_row",
          name: "#",
          field: "_row",
          width: 40,
          cssClass: "text-muted",
          resizable: false,
        },
        ...sheet.columns.map((col) => ({
          id: col,
          name: col,
          field: col,
          formatter: this.cellFormatter.bind(this),
          customTooltip: {
            formatter: (_row, _cell, _val, column, dataContext) => {
              const cell = dataContext[column.field];
              return cell && cell.error ? cell.error.message : false;
            },
          },
        })),
      ];

      const rows = sheet.rows.map((rowCells, rowIndex) => {
        const status = sheet.row_statuses?.[rowIndex];
        const rowNum = sheet.row_numbers?.[rowIndex] ?? rowIndex + 1;
        const defaultUpload = !this.isDuplicateStatus(status);
        const upload = this.overrides.has(rowNum) ? this.overrides.get(rowNum) : defaultUpload;
        const obj = {
          _row: rowNum + 1,       // display: spreadsheet row (header = 1, data starts at 2)
          _row_number: rowNum,    // JSONB row_number, key for overrides tracking
          _status: status,
          _upload: upload,
        };
        sheet.columns.forEach((col, colIndex) => {
          obj[col] = rowCells[colIndex];
        });
        return obj;
      });

      rows.getItemMetadata = (index) => {
        const status = rows[index]?._status;
        if (!status) return null;
        return { cssClasses: `row-status-${status}` };
      };

      const grid = new this.slick.SlickGrid(wrapper, rows, columns, {
        enableCellNavigation: true,
        enableColumnReorder: false,
        autosizeColsMode: this.slick.GridAutosizeColsMode.IgnoreViewport,
        rowHeight: ROW_HEIGHT,
      });

      grid.autosizeColumns();
      grid.registerPlugin(new this.slick.SlickCustomTooltip());

      if (hasStatuses) {
        grid.onClick.subscribe((e, args) => {
          if (columns[args.cell]?.id !== "_upload") return;
          e.stopPropagation();
          e.preventDefault();
          const row = rows[args.row];
          const newUpload = !row._upload;
          row._upload = newUpload;
          const defaultUpload = !this.isDuplicateStatus(row._status);
          if (newUpload !== defaultUpload) {
            this.overrides.set(row._row_number, newUpload);
          } else {
            this.overrides.delete(row._row_number);
          }
          if (this.isDuplicateStatus(row._status) && newUpload) {
            this.riskyOverrides.add(row._row_number);
          } else {
            this.riskyOverrides.delete(row._row_number);
          }
          this.renderWarningBanner();
          grid.invalidateRow(args.row);
          grid.render();
        });
      }

      this.grids.set(sheet.name, {
        grid,
        rows,
        columns,
        colIndexMap: Object.fromEntries(sheet.columns.map((c, i) => [c, i])),
      });
    }

    this.renderErrorLists(data);

    if (totalPages > 1) {
      this.renderPagination(totalPages, pagination.total_rows);
    }

    this.renderWarningBanner();

    this.gridHost.style.minHeight = "";
  }

  // Lives outside gridHost (see the warning target in the view, placed just
  // above the modal footer) so it stays visible next to the confirm button
  // rather than scrolling away with the grid, and persists across page loads.
  renderWarningBanner() {
    if (!this.hasWarningTarget) return;

    if (this.riskyOverrides.size === 0) {
      this.warningTarget.innerHTML = "";
      return;
    }

    const rowList = [...this.riskyOverrides]
      .sort((a, b) => a - b)
      .map((rowNumber) => `Row ${rowNumber + 1}`)
      .join(", ");

    this.warningTarget.innerHTML = `
      <div class="alert alert-danger mb-0">
        <strong>Warning:</strong> Please note the following red selected item(s) were previously imported
        into Slope and will override the data recorded. Please confirm if you want to proceed.
        <div class="mt-1"><strong>${rowList}</strong></div>
      </div>
    `;
  }

  async goToPage(page) {
    this.currentPage = page;
    await this.loadData();
  }

  renderPagination(totalPages, totalRows) {
    const nav = document.createElement("div");
    nav.className = "d-flex align-items-center justify-content-between mt-2";

    const info = document.createElement("span");
    info.className = "text-muted small";
    info.textContent = `Page ${this.currentPage} of ${totalPages} (${totalRows} rows total)`;

    const buttons = document.createElement("div");

    const prev = this.makeNavButton(
      "← Previous",
      "btn btn-sm btn-outline-secondary mr-1",
      this.currentPage <= 1,
      () => this.goToPage(this.currentPage - 1)
    );

    const next = this.makeNavButton(
      "Next →",
      "btn btn-sm btn-outline-secondary",
      this.currentPage >= totalPages,
      () => this.goToPage(this.currentPage + 1)
    );

    buttons.appendChild(prev);
    buttons.appendChild(next);
    nav.appendChild(info);
    nav.appendChild(buttons);
    this.gridHost.appendChild(nav);
  }

  // Builds a pagination nav button that stops the click from bubbling into
  // the grid and prevents any default form submission before running the
  // given callback.
  makeNavButton(label, className, disabled, onClick) {
    const button = document.createElement("button");
    button.type = "button";
    button.className = className;
    button.textContent = label;
    button.disabled = disabled;
    button.addEventListener("click", (e) => {
      e.stopPropagation();
      e.preventDefault();
      onClick();
    });
    return button;
  }

  beforeConfirm(event) {
    const riskyCount = this.riskyOverrides.size;
    if (riskyCount > 0) {
      const proceed = window.confirm(
        riskyCount === 1
          ? "1 row marked to upload already appears to have been imported previously. Import it anyway?"
          : `${riskyCount} rows marked to upload already appear to have been imported previously. Import them anyway?`
      );
      if (!proceed) {
        event.preventDefault();
        return;
      }
    }

    const overrides = [];
    for (const [rowNumber, upload] of this.overrides) {
      overrides.push({ row_number: rowNumber, upload });
    }
    const input = event.target.querySelector("input[name='row_overrides']");
    if (input) input.value = JSON.stringify(overrides);
  }

  cellFormatter(row, cell, value) {
    if (!value) return { text: "" };
    const res = { text: value.value ?? "" };
    if (value.error) {
      res.addClasses = value.error.blocking
        ? "bg-danger text-white"
        : "bg-warning";
    }
    return res;
  }

  renderErrorLists(data) {
    const blockingErrors = [];
    const warnings = [];

    for (const sheet of data.sheets) {
      sheet.rows.forEach((rowCells, rowIndex) => {
        sheet.columns.forEach((col, colIndex) => {
          const cell = rowCells[colIndex];
          if (cell && cell.error) {
            const entry = {
              sheet: sheet.name,
              column: col,
              row: rowIndex,
              message: cell.error.message,
              blocking: cell.error.blocking,
            };
            if (cell.error.blocking) {
              blockingErrors.push(entry);
            } else {
              warnings.push(entry);
            }
          }
        });
      });
    }

    if (blockingErrors.length > 0) {
      this.gridHost.appendChild(
        this.buildErrorList("Errors", blockingErrors, "alert-danger")
      );
    }

    if (warnings.length > 0) {
      this.gridHost.appendChild(
        this.buildErrorList("Warnings", warnings, "alert-warning")
      );
    }
  }

  buildErrorList(title, entries, alertClass) {
    const section = document.createElement("div");
    section.className = `mt-3 alert ${alertClass}`;
    const heading = document.createElement("strong");
    heading.textContent = title;
    section.appendChild(heading);
    const ol = document.createElement("ol");
    ol.className = "mb-0 mt-2";
    entries.forEach((entry) => {
      const li = document.createElement("li");
      const a = document.createElement("a");
      a.href = "#";
      a.className = "text-reset";
      a.appendChild(
        document.createTextNode(`Row ${entry.row + 2}, ${entry.column}: `)
      );
      const msgSpan = document.createElement("span");
      msgSpan.textContent = entry.message;
      a.appendChild(msgSpan);
      a.addEventListener("click", (ev) => {
        ev.preventDefault();
        this.scrollToError(entry);
      });
      li.appendChild(a);
      ol.appendChild(li);
    });
    section.appendChild(ol);
    return section;
  }

  scrollToError(entry) {
    const sheetData = this.grids.get(entry.sheet);
    if (!sheetData) return;
    const { grid, colIndexMap } = sheetData;
    const colIndex = colIndexMap[entry.column];
    const gridColIndex = colIndex !== undefined ? colIndex + 1 : 0;
    grid.scrollRowIntoView(entry.row);
    grid.scrollCellIntoView(entry.row, gridColIndex, true);
    grid.highlightRow(entry.row, 1000);
  }
}
